import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Breadcrumb, Card, Row, Col, Tag, Typography, Space, Spin, Empty, Button,
  Tooltip, Image, message, Timeline, Upload, Popconfirm,
} from 'antd';
import {
  HomeOutlined, UserOutlined, PhoneOutlined, MailOutlined, BankOutlined,
  FileTextOutlined, PaperClipOutlined, CheckCircleOutlined, StopOutlined,
  UserSwitchOutlined, FilePdfOutlined, CalendarOutlined, HistoryOutlined,
  DeleteOutlined, UploadOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { referralApi } from '../../../api/referral.api';
import AssignDoctorModal from '../components/AssignDoctorModal';
import RejectReferralModal from '../components/RejectReferralModal';
import ReferralReportModal from '../components/ReferralReportModal';
import { usePermission } from '../../../hooks/usePermission';
import { useAuthStore } from '../../../store/auth.store';
import { getApiErrorMessage, formatDate, formatDateTime, calculateAge } from '../../../utils/helpers';

const MAX_TOTAL_ATTACHMENT_SIZE = 32 * 1024 * 1024; // 32MB combined across all attachments

const AUDIT_ACTION_META = {
  referred: { label: 'Referral Created', icon: <FileTextOutlined />, color: 'blue' },
  assigned: { label: 'Doctor Assigned', icon: <UserSwitchOutlined />, color: 'green' },
  rejected: { label: 'Referral Rejected', icon: <StopOutlined />, color: 'red' },
  attachment_added: { label: 'Document(s) Uploaded', icon: <UploadOutlined />, color: 'cyan' },
  attachment_removed: { label: 'Document(s) Removed', icon: <DeleteOutlined />, color: 'orange' },
  report_submitted: { label: 'Report Submitted', icon: <FileTextOutlined />, color: 'purple' },
};

const { Text, Title } = Typography;

const isImageUrl = (url) => /\.(jpe?g|png)$/i.test(url || '');

// Uploaded-file names are generated as `referral-<timestamp>-<random>.<ext>`,
// so the exact upload time can be read back out of the URL/path without
// needing separate metadata storage.
const getUploadedAt = (url) => {
  const match = /referral-(\d{13})-/.exec(url || '');
  if (!match) { return null; }
  const timestamp = Number(match[1]);
  return Number.isFinite(timestamp) ? formatDateTime(new Date(timestamp)) : null;
};

const getDocumentName = (url) => (url || '').split('/').pop() || 'Document';

const InfoRow = ({ icon, label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
      {icon} {label}
    </Text>
    <Text style={{ fontSize: 14 }}>{value || '-'}</Text>
  </div>
);

const statusTag = (status, rejectionReason) => {
  if (status === 'rejected') {
    return (
      <Tooltip title={rejectionReason || 'Rejected'}>
        <Tag color="error" style={{ borderRadius: 9999 }}>Rejected</Tag>
      </Tooltip>
    );
  }
  if (status === 'accepted') {
    return <Tag color="success" style={{ borderRadius: 9999 }}>Accepted</Tag>;
  }
  return <Tag style={{ borderRadius: 9999 }}>Pending</Tag>;
};

const ReferralView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = usePermission();
  const { user } = useAuthStore();
  const currentDoctorId = user?.doctor?.toString?.() || user?.doctor;

  const [activeTab, setActiveTab] = useState('overview');
  const [assignOpen, setAssignOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['referral', id],
    queryFn: () => referralApi.getById(id),
  });

  const referral = data?.data;

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['referral-audit-logs', id],
    queryFn: () => referralApi.getAuditLogs(id),
    enabled: isAdmin && activeTab === 'assignment',
  });

  const auditLogs = auditData?.data || [];

  const assignMutation = useMutation({
    mutationFn: (doctorId) => referralApi.assign(id, doctorId),
    onSuccess: () => {
      queryClient.invalidateQueries(['referral', id]);
      queryClient.invalidateQueries(['referrals']);
      message.success('Referral assigned — the doctor has been notified by email');
      setAssignOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason) => referralApi.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(['referral', id]);
      queryClient.invalidateQueries(['referrals']);
      message.success('Referral rejected — the referring doctor has been notified by email');
      setRejectOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const addAttachmentsMutation = useMutation({
    mutationFn: (files) => referralApi.addAttachments(id, files),
    onSuccess: () => {
      queryClient.invalidateQueries(['referral', id]);
      queryClient.invalidateQueries(['referral-audit-logs', id]);
      queryClient.invalidateQueries(['referrals']);
      message.success('Document(s) uploaded');
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const removeAttachmentMutation = useMutation({
    mutationFn: (attachmentPath) => referralApi.removeAttachment(id, attachmentPath),
    onSuccess: () => {
      queryClient.invalidateQueries(['referral', id]);
      queryClient.invalidateQueries(['referral-audit-logs', id]);
      queryClient.invalidateQueries(['referrals']);
      message.success('Document removed');
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const submitReportMutation = useMutation({
    mutationFn: (values) => referralApi.submitReport(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries(['referral', id]);
      queryClient.invalidateQueries(['referral-audit-logs', id]);
      queryClient.invalidateQueries(['referrals']);
      message.success('Report submitted successfully');
      setReportOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (!referral) {
    return <Empty description="Referral not found" style={{ padding: 60 }} />;
  }

  const isRejected = referral.status === 'rejected';
  const isAssignedDoctor = referral.assignedDoctor?.id === currentDoctorId || referral.assignedDoctor?._id === currentDoctorId;
  // The referring doctor's patient documents are managed separately from the clinic
  // doctor's diagnosis/treatment report (see the "Upload Report" flow below).
  const canManageAttachments = isAdmin
    || referral.referringDoctor?.id === currentDoctorId || referral.referringDoctor?._id === currentDoctorId;

  const handleAttachmentUpload = (file, batch) => {
    const totalNewSize = batch.reduce((sum, f) => sum + f.size, 0);
    if (totalNewSize > MAX_TOTAL_ATTACHMENT_SIZE) {
      if (file === batch[0]) { message.error('Combined attachment size cannot exceed 32MB'); }
      return Upload.LIST_IGNORE;
    }
    if (file === batch[batch.length - 1]) {
      addAttachmentsMutation.mutate(batch);
    }
    return Upload.LIST_IGNORE;
  };

  const NAV_TABS = [
    { key: 'overview', label: 'Details', icon: <InfoCircleOutlined /> },
    { key: 'attachments', label: 'Patient Documents', icon: <PaperClipOutlined /> },
    ...(isAdmin ? [{ key: 'assignment', label: 'Audit Log', icon: <HistoryOutlined /> }] : []),
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 12 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/referrals')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Referred Children</span> },
          { title: referral.childName },
        ]}
      />

      {/* Summary card */}
      <Card
        style={{ borderRadius: 14, border: '1.5px solid var(--wl-primary-pale)', background: 'linear-gradient(135deg, var(--wl-primary-pale) 0%, var(--wl-card-bg) 100%)', marginBottom: 16 }}
        styles={{ body: { padding: '18px 22px' } }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={14}>
            <Title level={5} style={{ margin: 0, color: 'var(--wl-text)' }}>{referral.childName}</Title>
            <Space size={6} style={{ marginTop: 6 }} wrap>
              <Tag style={{ borderRadius: 9999, fontSize: 11, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>
                {calculateAge(referral.childDob) === '-' ? '-' : `${calculateAge(referral.childDob)} yrs`}
              </Tag>
              {statusTag(referral.status, referral.rejectionReason)}
              <Tag style={{ borderRadius: 9999, fontSize: 11 }}>{referral.referralId}</Tag>
            </Space>
          </Col>
          <Col xs={24} md={10} style={{ textAlign: 'right' }}>
            {isAdmin && (
              <Space wrap>
                <Button
                  icon={isRejected ? <CheckCircleOutlined /> : <UserSwitchOutlined />}
                  onClick={() => setAssignOpen(true)}
                  style={{ borderRadius: 9999, color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)' }}
                >
                  {isRejected ? 'Approve & Assign Doctor' : 'Assign to Doctor'}
                </Button>
                {!isRejected && (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={() => setRejectOpen(true)}
                    style={{ borderRadius: 9999 }}
                  >
                    Reject
                  </Button>
                )}
              </Space>
            )}
            {!isAdmin && isAssignedDoctor && !isRejected && (
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setReportOpen(true)}
                style={{ borderRadius: 9999, background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
              >
                Upload Report
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Nav tabs */}
      <div style={{ display: 'flex', gap: 3, background: 'var(--wl-primary-pale)', borderRadius: 12, padding: 4, marginBottom: 16 }}>
        {NAV_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, textAlign: 'center',
                padding: '9px 8px', borderRadius: 9, cursor: 'pointer',
                background: isActive ? 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))' : 'transparent',
                color: isActive ? '#fff' : 'var(--wl-primary)',
                fontWeight: 600, fontSize: 13,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                userSelect: 'none',
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </div>
          );
        })}
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Child / Patient" style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
              <InfoRow icon={<UserOutlined />} label="Name" value={referral.childName} />
              <InfoRow icon={<CalendarOutlined />} label="Date of Birth" value={formatDate(referral.childDob)} />
              <InfoRow icon={<BankOutlined />} label="Target Clinic" value={referral.targetClinic?.name} />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Parent / Guardian" style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
              <InfoRow icon={<UserOutlined />} label="Name" value={referral.parentName} />
              <InfoRow icon={<PhoneOutlined />} label="Contact Number" value={referral.parentContact} />
              <InfoRow icon={<MailOutlined />} label="Email" value={referral.parentEmail} />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Referring Doctor" style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
              <InfoRow icon={<UserOutlined />} label="Name" value={referral.referringDoctor?.name} />
              <InfoRow icon={<PhoneOutlined />} label="Contact Number" value={referral.referringDoctorContact} />
              <InfoRow icon={<MailOutlined />} label="Email" value={referral.referringDoctorEmail} />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Referral Details" style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
              <InfoRow icon={<CalendarOutlined />} label="Preferred Visit Timeframe" value={referral.preferredVisitTimeframe} />
              <InfoRow icon={<CalendarOutlined />} label="Referred On" value={formatDate(referral.createdAt)} />
              <InfoRow icon={<FileTextOutlined />} label="Reason for Referral" value={referral.reason} />
            </Card>
          </Col>
          {(referral.diagnosisSummary || referral.treatmentSummary) && (
            <Col xs={24}>
              <Card title="Clinic Doctor's Report" style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <InfoRow icon={<FileTextOutlined />} label="Diagnosis Summary" value={referral.diagnosisSummary} />
                  </Col>
                  <Col xs={24} md={12}>
                    <InfoRow icon={<FileTextOutlined />} label="Treatment Summary" value={referral.treatmentSummary} />
                  </Col>
                </Row>
                {(referral.reportAttachmentUrls || []).length > 0 && (
                  <>
                    <Text strong style={{ fontSize: 13, display: 'block', marginTop: 8, marginBottom: 12 }}>
                      <UserOutlined /> Uploaded by Clinic Doctor
                    </Text>
                    <Row gutter={[12, 12]}>
                      {referral.reportAttachmentUrls.map((url, idx) => (
                        <Col key={idx}>
                          <div style={{
                            width: 110, border: '1px solid var(--wl-primary-pale)',
                            borderRadius: 8, padding: 6, textAlign: 'center',
                          }}
                          >
                            <Text style={{ fontSize: 11, display: 'block', wordBreak: 'break-all' }} title={getDocumentName(url)}>
                              {getDocumentName(url)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                              {getUploadedAt(url) || '-'}
                            </Text>
                            {isImageUrl(url) ? (
                              <Image src={url} width={90} height={80} style={{ objectFit: 'contain' }} />
                            ) : (
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                <FilePdfOutlined style={{ fontSize: 28, color: 'var(--wl-primary)' }} />
                                <div style={{ fontSize: 11, marginTop: 4 }}>View</div>
                              </a>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Reports tab */}
      {activeTab === 'attachments' && (
        <Card
          style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
          extra={canManageAttachments && (
            <Upload
              multiple
              showUploadList={false}
              accept="application/pdf,image/jpeg,image/jpg,image/png"
              beforeUpload={handleAttachmentUpload}
            >
              <Button
                icon={<UploadOutlined />}
                loading={addAttachmentsMutation.isPending}
                type="primary"
                size="small"
                style={{ borderRadius: 9999, background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
              >
                Upload Document(s)
              </Button>
            </Upload>
          )}
        >
          {(referral.attachmentUrls || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <PaperClipOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
              <div style={{ color: '#666', fontSize: 14 }}>No patient documents were uploaded by the referring doctor</div>
            </div>
          ) : (
            <>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                <UserOutlined /> Uploaded by Referring Doctor
              </Text>
              <Row gutter={[16, 16]}>
                {referral.attachmentUrls.map((url, idx) => {
                  const attachmentPath = referral.attachments?.[idx];
                  return (
                    <Col xs={24} sm={12} md={8} key={idx}>
                      <Card
                        size="small"
                        style={{ borderRadius: 10, border: '1px solid var(--wl-primary-pale)', textAlign: 'center', position: 'relative' }}
                        styles={{ body: { padding: 12 } }}
                      >
                        {canManageAttachments && attachmentPath && (
                          <Popconfirm
                            title="Remove this document?"
                            onConfirm={() => removeAttachmentMutation.mutate(attachmentPath)}
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                            placement="topRight"
                          >
                            <Button
                              shape="circle"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              loading={removeAttachmentMutation.isPending}
                              style={{ position: 'absolute', top: 6, right: 6, zIndex: 1, background: '#fff2f0', border: '1px solid #ff4d4f', color: '#ff4d4f' }}
                            />
                          </Popconfirm>
                        )}
                        <Text style={{ fontSize: 12, display: 'block', wordBreak: 'break-all' }} title={getDocumentName(url)}>
                          {getDocumentName(url)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                          {getUploadedAt(url) || '-'}
                        </Text>
                        {isImageUrl(url) ? (
                          <Image src={url} style={{ maxHeight: 140, objectFit: 'contain' }} />
                        ) : (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '24px 0' }}>
                            <FilePdfOutlined style={{ fontSize: 36, color: 'var(--wl-primary)' }} />
                            <div style={{ marginTop: 8, fontSize: 12 }}>View Document</div>
                          </a>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Card>
      )}

      {/* Audit Log tab (admin only) */}
      {activeTab === 'assignment' && isAdmin && (
        <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}>
          <Space direction="vertical" size={4} style={{ marginBottom: 20, width: '100%' }}>
            <Text strong style={{ fontSize: 14 }}>Current Status</Text>
            <Space size={8}>
              {statusTag(referral.status, referral.rejectionReason)}
              {referral.assignedDoctor?.name && (
                <Text type="secondary" style={{ fontSize: 13 }}>Assigned to Dr. {referral.assignedDoctor.name}</Text>
              )}
            </Space>
          </Space>

          {auditLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : auditLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <HistoryOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
              <div style={{ color: '#666', fontSize: 14 }}>No audit history recorded for this referral yet</div>
            </div>
          ) : (
            <Timeline
              items={auditLogs.map((log) => {
                const meta = AUDIT_ACTION_META[log.action] || { label: log.action, icon: <FileTextOutlined />, color: 'gray' };
                return {
                  key: log.id || log._id,
                  color: meta.color,
                  dot: meta.icon,
                  children: (
                    <div style={{ paddingBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>{meta.label}</Text>
                      <div style={{ fontSize: 13, color: '#555', margin: '2px 0' }}>{log.details}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {log.performedByName || 'Unknown'}{log.performedByRole ? ` (${log.performedByRole})` : ''} · {formatDateTime(log.createdAt)}
                      </Text>
                    </div>
                  ),
                };
              })}
            />
          )}
        </Card>
      )}

      <AssignDoctorModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={(doctorId) => assignMutation.mutate(doctorId)}
        record={referral}
        loading={assignMutation.isPending}
      />

      <RejectReferralModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={(reason) => rejectMutation.mutate(reason)}
        record={referral}
        loading={rejectMutation.isPending}
      />

      <ReferralReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={(values) => submitReportMutation.mutate(values)}
        loading={submitReportMutation.isPending}
      />
    </div>
  );
};

export default ReferralView;
